import type { Comparable } from '../field-types/card';
import type { Operator } from '../format/pieces';

type ComparingOperator = Exclude<Operator, 'isFilled' | 'isEmpty'>;

const HOLD_WHEN_A_SIDE_IS_EMPTY: Operator[] = ['notEquals', 'noneOf', 'notContains'];

export const holdsWhenEmpty = (operator: ComparingOperator): boolean => {
  return HOLD_WHEN_A_SIDE_IS_EMPTY.includes(operator);
};

type Holds = (left: Comparable, right: Comparable) => boolean | undefined;

interface Comparing {
  holds: Holds;
}

export const isOptionSet = (value: Comparable | undefined): value is string[] => Array.isArray(value);

const onOptions = (holds: (left: string[], right: string[]) => boolean): Comparing => {
  return {
    holds: (left, right) => (isOptionSet(left) && isOptionSet(right) ? holds(left, right) : undefined),
  };
};

const onOrder = (holds: (order: number) => boolean): Comparing => {
  return {
    holds: (left, right) => {
      if (typeof left === 'number' && typeof right === 'number') return holds(Math.sign(left - right));
      if (typeof left === 'string' && typeof right === 'string') return holds(left < right ? -1 : left > right ? 1 : 0);
      return undefined;
    },
  };
};

const onText = (holds: (left: string, right: string) => boolean): Comparing => {
  return {
    holds: (left, right) => (typeof left === 'string' && typeof right === 'string' ? holds(left, right) : undefined),
  };
};

const COMPARING: Record<ComparingOperator, Comparing> = {
  anyOf: onOptions((selected, list) => selected.some(id => list.includes(id))),
  noneOf: onOptions((selected, list) => !selected.some(id => list.includes(id))),
  allOf: onOptions((selected, list) => list.every(id => selected.includes(id))),
  equals: onOrder(order => order === 0),
  notEquals: onOrder(order => order !== 0),
  lessThan: onOrder(order => order < 0),
  lessThanOrEqual: onOrder(order => order <= 0),
  greaterThan: onOrder(order => order > 0),
  greaterThanOrEqual: onOrder(order => order >= 0),
  contains: onText((text, part) => text.includes(part)),
  notContains: onText((text, part) => !text.includes(part)),
  startsWith: onText((text, start) => text.startsWith(start)),
  endsWith: onText((text, end) => text.endsWith(end)),
};

export const operatorHolds = (
  operator: ComparingOperator,
  left: Comparable,
  right: Comparable,
): boolean | undefined => {
  return COMPARING[operator].holds(left, right);
};
